import { useState, type ReactNode } from 'react';
import type { HTMLAttributes } from 'react';
import styles from './DataTable.module.css';
import { TableCell } from '../../basic/TableCell/TableCell';
import { TableRow } from '../../basic/TableRow/TableRow';
import { Skeleton } from '../../basic/Skeleton/Skeleton';
import { MobileCard } from '../MobileCard/MobileCard';
import type { MobileCardDetail } from '../MobileCard/MobileCard';

export interface Column {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

export interface MobileConfig {
  primaryField: string;
  secondaryField: string;
  expandableFields: string[];
}

export interface DataTableProps extends HTMLAttributes<HTMLDivElement> {
  columns: Column[];
  data: Array<Record<string, any>>;
  mobileConfig?: MobileConfig;
  renderCell?: (key: string, value: any, row: Record<string, any>) => ReactNode;
  onRowClick?: (row: Record<string, any>, index: number) => void;
  isLoading?: boolean;
}

export function DataTable({
  columns,
  data,
  mobileConfig,
  renderCell,
  onRowClick,
  isLoading,
  className,
  ...props
}: DataTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const renderCellContent = (key: string, value: any, row: Record<string, any>) => {
    if (renderCell) {
      return renderCell(key, value, row);
    }
    return value ?? '-';
  };

  const getCellVariant = (_key: string, value: any): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      if (lowerValue.includes('active') || lowerValue.includes('success')) {
        return 'success';
      }
      if (lowerValue.includes('bloqueada') || lowerValue.includes('error')) {
        return 'error';
      }
      if (lowerValue.includes('pending') || lowerValue.includes('warning')) {
        return 'warning';
      }
    }
    return 'default';
  };

  const classes = [styles.dataTable, className].filter(Boolean).join(' ');

  return (
    <div className={classes} {...props}>
      {/* Desktop: Table view */}
      <div className={styles.desktopView}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr className={styles.headerRow}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  as="th"
                  align={col.align || 'left'}
                  style={col.width ? { width: col.width } : undefined}
                  className={styles.headerCell}
                >
                  {col.label}
                </TableCell>
              ))}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {isLoading ? (
              Array(5).fill(0).map((_, idx) => (
                <TableRow key={`skeleton-row-${idx}`}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align || 'left'} className={styles.bodyCell}>
                      <Skeleton width="80%" height="20px" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              data.map((row, idx) => (
                <TableRow 
                  key={idx}
                  onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                  className={onRowClick ? styles.clickableRow : undefined}
                >
                  {columns.map((col) => {
                    const value = row[col.key];
                    const cellContent = renderCellContent(col.key, value, row);
                    const variant = getCellVariant(col.key, value);
                    const isMasked = typeof value === 'string' && value.includes('••••');

                    return (
                      <TableCell
                        key={col.key}
                        align={col.align || 'left'}
                        variant={variant !== 'default' ? variant : undefined}
                        isMasked={isMasked}
                        className={styles.bodyCell}
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile: Card view */}
      {mobileConfig && (
        <div className={styles.mobileView} data-testid="mobile-view">
          {isLoading ? (
            Array(3).fill(0).map((_, idx) => (
              <div key={`skeleton-card-${idx}`} className={styles.skeletonCard}>
                <Skeleton width="100%" height="80px" />
              </div>
            ))
          ) : (
            data.map((row, idx) => {
              const details: MobileCardDetail[] = mobileConfig.expandableFields.map((field) => {
                const column = columns.find((c) => c.key === field);
                const value = row[field];
                const cellContent = renderCellContent(field, value, row);
                const variant = getCellVariant(field, value);

                return {
                  label: column?.label || field,
                  value: cellContent,
                  variant: variant !== 'default' ? variant : undefined,
                };
              });

              const primaryValue = row[mobileConfig.primaryField];
              const secondaryValue = row[mobileConfig.secondaryField];
              const primaryContent = renderCellContent(mobileConfig.primaryField, primaryValue, row);
              const secondaryContent = renderCellContent(mobileConfig.secondaryField, secondaryValue, row);

              return (
                <MobileCard
                  key={idx}
                  id={`data-table-row-${idx}`}
                  header={{
                    primary: primaryContent ?? '',
                    secondary: secondaryContent ?? '',
                  }}
                  details={details}
                  isExpanded={expandedRows.has(idx)}
                  onToggle={() => toggleRow(idx)}
                  onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

DataTable.displayName = 'DataTable';
